"use client";

import React, { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Minus, X } from '@/components/icons';

interface QuantitySelectorProps {
  cartItemId: string;
  quantity: number;
  onUpdateQuantity: (cartItemId: string, quantityCalculator: (current: number) => number) => void;
  onPlayBeep: (type: 'add' | 'decrease') => void;
  onVibrate: (type: 'add' | 'decrease') => void;
  disabled?: boolean;
}

export const QuantitySelector = React.memo(function QuantitySelectorComponent({
  cartItemId,
  quantity,
  onUpdateQuantity,
  onPlayBeep,
  onVibrate,
  disabled = false,
}: QuantitySelectorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(String(quantity));
  const inputRef = useRef<HTMLInputElement>(null);
  const quantityText = String(Math.max(0, Number(quantity) || 0));
  const displayDigits = Math.max(2, quantityText.length);
  const editingDigits = Math.max(2, quantityText.length, inputValue.length);
  const displayWidth = `calc(${displayDigits}ch + 0.7rem)`;
  const inputWidth = `calc(${editingDigits}ch + 0.9rem)`;

  useEffect(() => {
    if (!isEditing) {
      setInputValue(String(quantity));
    }
  }, [quantity, isEditing]);

  // Auto-focus cuando entra en modo edición
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  // Seleccionar todo el texto al enfocarse
  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const handleDecrement = () => {
    if (disabled) return;
    const nextQuantity = Math.max(0, quantity - 1);
    setInputValue(String(nextQuantity));
    onVibrate('decrease');
    onPlayBeep('decrease');
    onUpdateQuantity(cartItemId, () => nextQuantity);
  };

  const handleIncrement = () => {
    if (disabled) return;
    const nextQuantity = quantity + 1;
    setInputValue(String(nextQuantity));
    onVibrate('add');
    onPlayBeep('add');
    onUpdateQuantity(cartItemId, () => nextQuantity);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Solo permitir números
    const val = e.target.value.replace(/\D/g, '');
    setInputValue(val);
  };

  const handleInputConfirm = () => {
    const parsedQty = parseInt(inputValue) || 0;
    const newQty = Math.max(0, parsedQty);

    if (newQty !== quantity) {
      // Play sound based on whether incrementing or decrementing
      const type = newQty > quantity ? 'add' : 'decrease';
      onVibrate(type);
      onPlayBeep(type);
      onUpdateQuantity(cartItemId, () => newQty);
    }
    setIsEditing(false);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputConfirm();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };

  const handleInputBlur = () => {
    handleInputConfirm();
  };

  const handleDisplayClick = () => {
    setInputValue(String(quantity));
    setIsEditing(true);
  };

  if (isEditing) {
    return (
      <div className={`flex items-center gap-1 bg-background dark:bg-slate-950 p-1.5 rounded-full shadow-lg border border-primary/40 dark:border-primary/50 backdrop-blur-sm ${disabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleDecrement}
          disabled={disabled}
          className="h-7 w-7 p-0 rounded-full hover:bg-primary/10 text-primary hover:text-primary transition-all active:scale-75 duration-75 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          {quantity === 1 ? (
            <X className="h-3.5 w-3.5" />
          ) : (
            <Minus className="h-3.5 w-3.5" />
          )}
        </Button>

        <input
          ref={inputRef}
          type="number"
          inputMode="numeric"
          min="0"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleInputKeyDown}
          onBlur={handleInputBlur}
          onFocus={handleFocus}
          disabled={disabled}
          className="hide-number-spin font-bold text-sm h-7 text-center border-0 bg-primary/5 dark:bg-primary/20 rounded-md px-1 outline-none text-foreground focus:ring-1 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed tabular-nums"
          style={{ width: inputWidth, minWidth: '2.25rem' }}
        />

        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={handleIncrement}
          disabled={disabled}
          className="h-7 w-7 p-0 rounded-full hover:bg-primary/10 text-primary hover:text-primary-foreground transition-all active:scale-75 duration-75 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
        >
          <Plus className="h-3.5 w-3.5" />
        </Button>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-1 bg-background dark:bg-slate-950 p-1.5 rounded-full shadow-lg border border-primary/40 dark:border-primary/50 backdrop-blur-sm ${disabled ? 'opacity-50' : ''}`}>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={handleDecrement}
        disabled={disabled}
        className="h-7 w-7 p-0 rounded-full hover:bg-primary/10 text-primary hover:text-primary transition-all active:scale-75 duration-75 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
      >
        {quantity === 1 ? (
          <X className="h-3.5 w-3.5" />
        ) : (
          <Minus className="h-3.5 w-3.5" />
        )}
      </Button>

      <div
        onClick={disabled ? undefined : handleDisplayClick}
        className={`font-bold text-sm h-7 text-center cursor-pointer hover:text-primary active:text-primary/70 transition-colors text-foreground hover:scale-110 active:scale-95 duration-75 flex items-center justify-center bg-primary/5 dark:bg-primary/10 rounded-md select-none tabular-nums px-1 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        role="button"
        tabIndex={disabled ? -1 : 0}
        style={{ width: displayWidth, minWidth: '2.25rem' }}
      >
        {quantity}
      </div>

      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={handleIncrement}
        disabled={disabled}
        className="h-7 w-7 p-0 rounded-full hover:bg-primary/10 text-primary hover:text-primary-foreground transition-all active:scale-75 duration-75 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
      >
        <Plus className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
});
